/**
 * Phone entry for the WhatsApp sign-ins (owner 2026-10-04: "+91 by default, difficult to type +").
 * The country code is a picker (default +91); the person types only their number on a numeric keypad.
 * A pasted full number ("+971 50…", "0091 98…") still works: it is split and the code is selected.
 */
export interface CountryCode { code: string; label: string }

/** India first (most clients), then the Gulf (NRI couples), then the rest seen in bookings. */
export const COUNTRY_CODES: readonly CountryCode[] = [
  { code: "+91", label: "India +91" },
  { code: "+971", label: "UAE +971" },
  { code: "+974", label: "Qatar +974" },
  { code: "+966", label: "Saudi Arabia +966" },
  { code: "+965", label: "Kuwait +965" },
  { code: "+968", label: "Oman +968" },
  { code: "+973", label: "Bahrain +973" },
  { code: "+44", label: "UK +44" },
  { code: "+1", label: "USA / Canada +1" },
  { code: "+61", label: "Australia +61" },
  { code: "+64", label: "New Zealand +64" },
  { code: "+65", label: "Singapore +65" },
  { code: "+60", label: "Malaysia +60" },
  { code: "+49", label: "Germany +49" },
  { code: "+353", label: "Ireland +353" },
];

export const DEFAULT_CODE = "+91";

/** Longest-prefix match of a full "+…" number against the list. */
function matchCode(full: string): string | null {
  const hit = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length).find((c) => full.startsWith(c.code));
  return hit ? hit.code : null;
}

/**
 * What the person typed or pasted into the number box → { code, local }.
 * - digits only: keep the current code; a leading 0 (trunk prefix, "098…") is dropped.
 * - "+…" or "00…": a full international number — select its code when it is in the list,
 *   otherwise keep the whole thing as an "other" number (code "").
 */
export function splitPhoneInput(raw: string, currentCode: string): { code: string; local: string } {
  let v = (raw || "").replace(/[^\d+]/g, "");
  if (v.startsWith("00")) v = "+" + v.slice(2);
  if (v.startsWith("+")) {
    const digits = "+" + v.slice(1).replace(/\+/g, "");
    const code = matchCode(digits);
    if (code) return { code, local: digits.slice(code.length) };
    return { code: "", local: digits };
  }
  const digits = v.replace(/\+/g, "");
  return { code: currentCode, local: currentCode ? digits.replace(/^0+/, "") : digits };
}

/** The E.164-style number sent to the server ("+919876543210"). */
export function fullPhone(code: string, local: string): string {
  const d = (local || "").replace(/[^\d+]/g, "");
  if (!code) return d.startsWith("+") ? d : d ? "+" + d : "";
  return d ? code + d.replace(/\D/g, "") : "";
}
